import { z } from 'zod';
import { LIMITS } from './constants.js';

export const DIGITAL_FIELD_TYPES = ['text', 'textarea', 'phone', 'email', 'id', 'companyId', 'date', 'number', 'checkbox', 'select', 'signature'] as const;

/**
 * A signature answer is the drawing itself, inline in the document's values.
 *
 * The closing signature is written to a file because the certificate and the
 * stamping path need it as bytes; these are answers, and they live where every
 * other answer lives, so a document and what was filled into it stay one row.
 */
export const DOCUMENT_SIGNATURE_PREFIX = 'data:image/png;base64,';

/** How many bytes a base64 payload decodes to, without decoding it. */
function base64Bytes(payload: string): number {
  const padding = payload.endsWith('==') ? 2 : payload.endsWith('=') ? 1 : 0;
  return Math.floor((payload.length * 3) / 4) - padding;
}
const key = 'validation.digitalDocument.structure';
export const digitalParagraphFormatSchema = z.object({
  bold: z.boolean().optional(), underline: z.boolean().optional(),
  align: z.enum(['left','center','right']).optional(), numbered: z.number().int().min(1).max(10_000).optional(),
  divider: z.boolean().optional(),
}).strict();
const contentBlockSchema = z.object({ id: z.string().uuid(key), type: z.enum(['heading', 'paragraph']), text: z.string().trim().min(1, key).max(50_000, key), format: digitalParagraphFormatSchema.optional() }).strict();
const fieldBlockSchema = z.object({
  id: z.string().uuid(key), type: z.literal('field'), fieldType: z.enum(DIGITAL_FIELD_TYPES),
  label: z.string().trim().min(1, key).max(120, key), required: z.boolean(),
  inline: z.boolean().optional(),
  purpose: z.enum(['city','yesNo','agreement']).optional(),
  placeholder: z.string().trim().max(240, key).optional(),
  options: z.array(z.string().trim().min(1, key).max(120, key)).max(20, key).optional(),
}).strict();
export const digitalDocumentBlockSchema = z.discriminatedUnion('type', [contentBlockSchema.extend({ type: z.literal('heading') }), contentBlockSchema.extend({ type: z.literal('paragraph') }), fieldBlockSchema]);
export type DigitalDocumentBlock = z.infer<typeof digitalDocumentBlockSchema>;
export type DigitalDocumentField = Extract<DigitalDocumentBlock, { type: 'field' }>;

export const digitalDocumentSchema = z.object({ version: z.literal(1), blocks: z.array(digitalDocumentBlockSchema).min(1, key).max(60, key) }).strict().superRefine((document, ctx) => {
  const ids = new Set<string>();
  let length = 0;
  document.blocks.forEach((block, index) => {
    if (ids.has(block.id)) ctx.addIssue({ code: 'custom', message: key, path: ['blocks', index, 'id'] });
    ids.add(block.id);
    if (block.type !== 'field') {
      length += block.text.length;
      if(block.format?.divider && (block.text!=='—'||block.format.numbered))ctx.addIssue({code:'custom',message:key,path:['blocks',index,'format']});
      return;
    }
    length += block.label.length + (block.placeholder?.length ?? 0) + (block.options?.join('').length ?? 0);
    if (block.fieldType === 'select' && (!block.options?.length || new Set(block.options).size !== block.options.length)) {
      ctx.addIssue({ code: 'custom', message: key, path: ['blocks', index, 'options'] });
    }
    if(block.purpose==='city'&&block.fieldType!=='text')ctx.addIssue({code:'custom',message:key,path:['blocks',index,'purpose']});
    if(block.purpose==='yesNo'&&(block.fieldType!=='select'||block.options?.length!==2))ctx.addIssue({code:'custom',message:key,path:['blocks',index,'purpose']});
    if(block.purpose==='agreement'&&(block.fieldType!=='checkbox'||!block.required))ctx.addIssue({code:'custom',message:key,path:['blocks',index,'purpose']});
  });
  if (document.blocks.filter(block => block.type === 'field' && block.fieldType === 'signature').length > LIMITS.maxDocumentSignatureFields) {
    ctx.addIssue({ code: 'custom', message: key, path: ['blocks'] });
  }
  const references = document.blocks.filter(block => block.type !== 'field').flatMap(block => splitDigitalText(block.text).flatMap(part => 'fieldId' in part ? [part.fieldId] : []));
  const inlineFields = document.blocks.filter(block => block.type === 'field' && block.inline);
  if (references.some(id => !inlineFields.some(field => field.id === id)) || inlineFields.some(field => references.filter(id => id === field.id).length !== 1)) {
    ctx.addIssue({ code: 'custom', message: key, path: ['blocks'] });
  }
  if (length > 50_000) ctx.addIssue({ code: 'custom', message: key, path: ['blocks'] });
});
export type DigitalDocument = z.infer<typeof digitalDocumentSchema>;
export type DigitalDocumentValues = Record<string, string | boolean>;

/*
 * The shape of an answer set, before anything is known about the document it
 * answers. The ceiling is a drawn signature's, because a signature arrives here
 * as its own pixels; a typed answer's real limit is 4,000 characters and is
 * enforced against the saved definition below, where the field's type is known.
 */
export const documentValuesSchema = z.record(z.string().uuid('validation.digitalDocument.unknownField'), z.union([z.string().max(LIMITS.maxDocumentSignatureBytes * 2, 'validation.digitalDocument.value'), z.boolean()])).refine((values) => Object.keys(values).length <= 60, 'validation.digitalDocument.value');

/** Shared by the client and server; the server validates against its stored definition. */
export function validateDocumentValues(document: DigitalDocument | null, values: DigitalDocumentValues): Record<string, string> {
  const errors: Record<string, string> = {};
  const fields = new Map((document?.blocks ?? []).filter((block): block is DigitalDocumentField => block.type === 'field').map((field) => [field.id, field]));
  for (const id of Object.keys(values)) if (!fields.has(id)) errors[id] = 'validation.digitalDocument.unknownField';
  for (const [id, field] of fields) {
    const value = values[id];
    const blank = value === undefined || (typeof value === 'string' && value.trim() === '') || (field.fieldType === 'checkbox' && value === false);
    if (blank) { if (field.required) errors[id] = 'validation.digitalDocument.required'; continue; }
    if (field.fieldType === 'checkbox') { if (typeof value !== 'boolean') errors[id] = 'validation.digitalDocument.value'; continue; }
    if (field.fieldType === 'signature') {
      const payload = typeof value === 'string' && value.startsWith(DOCUMENT_SIGNATURE_PREFIX) ? value.slice(DOCUMENT_SIGNATURE_PREFIX.length) : null;
      // Shape and size only. Whether these bytes are a PNG anything can draw is
      // settled on the server, where they are decoded.
      if (payload === null || !/^[A-Za-z0-9+/]+={0,2}$/.test(payload) || base64Bytes(payload) > LIMITS.maxDocumentSignatureBytes) errors[id] = 'validation.digitalDocument.signature';
      continue;
    }
    if (typeof value !== 'string' || value.length > 4000) { errors[id] = 'validation.digitalDocument.value'; continue; }
    const text = value.trim();
    let valid = true;
    switch (field.fieldType) {
      case 'email': valid = z.string().email().safeParse(text).success; break;
      case 'phone': valid = /^\+?[\d ()-]+$/.test(text) && text.replace(/\D/g, '').length >= 7 && text.replace(/\D/g, '').length <= 15; break;
      case 'id': case 'companyId': valid = /^\d{9}$/.test(text); break;
      case 'date': { const date = new Date(`${text}T00:00:00.000Z`); valid = /^\d{4}-\d{2}-\d{2}$/.test(text) && Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === text; break; }
      case 'number': valid = /^-?(?:\d+(?:\.\d+)?|\.\d+)$/.test(text) && Number.isFinite(Number(text)); break;
      case 'select': valid = field.options?.includes(text) ?? false; break;
    }
    if (!valid) errors[id] = `validation.digitalDocument.${field.fieldType === 'select' ? 'option' : field.fieldType}`;
  }
  return errors;
}

/** Does not invent values for unanswered fields or accept unknown field IDs. */
export function normalizeDocumentValues(document: DigitalDocument | null, values: DigitalDocumentValues): DigitalDocumentValues {
  const normalized: DigitalDocumentValues = {};
  for (const block of document?.blocks ?? []) {
    if (block.type !== 'field' || !Object.prototype.hasOwnProperty.call(values, block.id)) continue;
    const value = values[block.id];
    if (typeof value === 'string') normalized[block.id] = value.trim().replace(/\r\n?/g, '\n');
    else if (typeof value === 'boolean') normalized[block.id] = value;
  }
  return normalized;
}

/** Stable placeholders are stored separately from client answers, never as HTML. */
export function splitDigitalText(text: string): Array<{ text: string } | { fieldId: string }> {
  const parts: Array<{ text: string } | { fieldId: string }> = [];
  const pattern = /\{\{([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\}\}/gi;
  let offset = 0;
  for (const match of text.matchAll(pattern)) {
    if (match.index > offset) parts.push({ text: text.slice(offset, match.index) });
    parts.push({ fieldId: match[1]! });
    offset = match.index + match[0].length;
  }
  if (offset < text.length) parts.push({ text: text.slice(offset) });
  return parts;
}
